import React from "react";

import { baseURL } from "Dashboard/EmployerUrls";
import ChooseAccidentCoveragePage from "EmployeeApplication/ChooseAccidentCoveragePage";
import ChooseLifeCoveragePage from "EmployeeApplication/ChooseLifeCoveragePage";
import ChooseLongTermDisabilityCoveragePage from "EmployeeApplication/ChooseLongTermDisabilityCoveragePage";
import ChooseShortTermDisabilityCoveragePage from "EmployeeApplication/ChooseShortTermDisabilityCoveragePage";
import ChooseTermLifeCoveragePage from "EmployeeApplication/ChooseTermLifeCoveragePage";
import ChooseWholeLifeCoveragePage from "EmployeeApplication/ChooseWholeLifeCoveragePage";
import ChooseMedicalCoveragePage from "EmployeeApplication/pages/ChooseMedicalCoveragePage";
import { BrokerFormFooter } from "Features/BrokerForm";
import isIntuit from "Intuit/isIntuit";
import { Helmet } from "react-helmet";
import { useSelector } from "react-redux";
import { Switch, Route, Link, Redirect } from "react-router-dom";
import AnalyticsTracking from "Utils/AnalyticsTracking";
import companyToken from "Utils/companyToken";
import { isDeclinedCompletedPolicy } from "Utils/EmployeeUtils";
import { formatNameFL } from "Utils/formatName";
import { isNonPartner } from "Utils/isPartner";
import { BasicLoading } from "ZeroRuby";
import Heartbeat from "ZeroRuby/snippets/heartbeat";

import BodyText from "Common/BodyText";
import Footer from "Common/Footer";
import { Grid, Row, Col } from "Common/Grid";
import { Div } from "Common/HTML";
import { useSiteConfig } from "Common/Redux/siteConfig";
import ScrollToTop from "Common/ScrollToTop";
import ThemeInstaller from "Common/ThemeInstaller";
import ResponsiveTopBar from "Common/TopBar/ResponsiveTopBar";
import TopBarAccountLinks from "Common/TopBar/TopBarAccountLinks";

import AdditionalInformationPage from "./AdditionalInformationPage";
import {
  getAvailablePlans,
  useGetApplicationQuery,
  getEmployeeForm,
  getEmployeeApplication,
  getAdminMode,
  getSiteConfig,
  getHealthAccountBenefitStatus,
} from "./api";
import BasicInformationPage from "./BasicInformationPage";
import ChooseCoveragePage from "./ChooseCoveragePage";
import DonePage from "./DonePage";
import FamilyInformationPage from "./FamilyInformationPage";
import Page from "./Page";
import HealthAccountInformationPage from "./pages/HealthAccountInformationPage";
import ProcessOverview from "./ProcessOverview";
import SignaturePage from "./SignaturePage";
import {
  adminEmployeeParam,
  basicInformationURL,
  familyInformationURL,
  chooseMedicalPlanURL,
  chooseDentalPlanURL,
  chooseVisionPlanURL,
  chooseAccidentPlanURL,
  chooseLifePlanURL,
  chooseTermLifePlanURL,
  chooseWholeLifePlanURL,
  chooseShortTermDisabilityPlanURL,
  chooseLongTermDisabilityPlanURL,
  healthAccountInformationURL,
  additionalInformationURL,
  signURL,
  doneURL,
  inNewEnrollmentFlow,
  nestedEmployeeApplicationRoutes,
} from "./urls";

const JumpPage = ({ isAdminMode, employeeFormId }) => (
  <Page title="Jump to step">
    <ul>
      <li>
        <Link to={basicInformationURL(isAdminMode, false, employeeFormId)}>Basic Information</Link>
      </li>
      <li>
        <Link to={familyInformationURL(isAdminMode, false, employeeFormId)}>Family Information</Link>
      </li>
      <li>
        <Link to={chooseMedicalPlanURL(isAdminMode, false, employeeFormId)}>Choose Medical Plan</Link>
      </li>
      <li>
        <Link to={chooseDentalPlanURL(isAdminMode, false, employeeFormId)}>Choose Dental Plan</Link>
      </li>
      <li>
        <Link to={chooseVisionPlanURL(isAdminMode, false, employeeFormId)}>Choose Vision Plan</Link>
      </li>
      <li>
        <Link to={chooseAccidentPlanURL(isAdminMode, false, employeeFormId)}>Choose Accident Plan</Link>
      </li>
      <li>
        <Link to={chooseLifePlanURL(isAdminMode, false, employeeFormId)}>Choose Life Plan</Link>
      </li>
      <li>
        <Link to={chooseTermLifePlanURL(isAdminMode, false, employeeFormId)}>Choose Term Life Plan</Link>
      </li>
      <li>
        <Link to={chooseWholeLifePlanURL(isAdminMode, false, employeeFormId)}>Choose Whole Life Plan</Link>
      </li>
      <li>
        <Link to={chooseShortTermDisabilityPlanURL(isAdminMode, false, employeeFormId)}>Choose Short Term Disability Plan</Link>
      </li>
      <li>
        <Link to={chooseLongTermDisabilityPlanURL(isAdminMode, false, employeeFormId)}>Choose Long Term Disability Plan</Link>
      </li>
      <li>
        <Link to={healthAccountInformationURL(isAdminMode, false, employeeFormId)}>Health Account Information</Link>
      </li>
      <li>
        <Link to={additionalInformationURL(isAdminMode, false, employeeFormId)}>Additional Information</Link>
      </li>
      <li>
        <Link to={signURL(isAdminMode, false, employeeFormId)}>Signature</Link>
      </li>
      <li>
        <Link to={doneURL(isAdminMode, false, employeeFormId)}>Done</Link>
      </li>
    </ul>
  </Page>
);

const NotFoundPage = () => (
  <Page title="Whoops! Bad Link!">
    <BodyText componentClass="p" size="large" agent>
      You've clicked on a link we don't know how to handle. Please contact support for assistance.
    </BodyText>
  </Page>
);

const EmployeeApplication = ({ match }) => {
  const employeeFormId = match.params[adminEmployeeParam];
  const { isLoading: isLoadingApplication } = useGetApplicationQuery();
  const employeePolicy = useSelector(getEmployeeApplication);
  const employeeForm = useSelector(getEmployeeForm);
  const isAdminMode = useSelector(getAdminMode);
  const { branding, partnerLogo, partnerName } = useSelector(getSiteConfig) || {};
  const { isLoading: isLoadingSiteConfig } = useSiteConfig();
  const availablePlans = useSelector(getAvailablePlans);
  const healthAccountBenefitStatus = useSelector(getHealthAccountBenefitStatus);
  const isRenewal = useSelector(getEmployeeApplication)?.isRenewal;
  const isLoading = isLoadingApplication || isLoadingSiteConfig;

  if (employeeForm && !employeeForm.isEligible) {
    return <Redirect to={baseURL} />;
  }

  /**
   * The account entity does not appear to be in the store for the EmployeeApplication.
   * To account for Admin Mode, where a company admin fills out an employee application,
   * I am hiding the account links entirely. The company admin can exit the application
   * to return back to the Employer Application.
   */

  const rightContent = [];

  if (!isAdminMode) {
    rightContent.push(
      <TopBarAccountLinks
        showSwitchCompany={companyToken()}
        showLogout={isIntuit() || isNonPartner()}
        menuTitle={formatNameFL(employeeForm) || employeeForm?.email}
        showSupport={!!employeeForm && !employeeForm?.benefitsAdmin}
      />,
    );
  }

  const hasDeclinedCompletedPolicy = employeePolicy && isDeclinedCompletedPolicy(employeePolicy);

  // If we are in the SPA for the New Enrollment Flow, then we don't need header/footers/other globals
  if (inNewEnrollmentFlow()) {
    return (
      <Div className="min-h-dvh">
        <Grid>
          <Row>
            <Col md={12} mb={3} mt={4}>
              <ProcessOverview />
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              {isLoading ? (
                <BasicLoading />
              ) : (
                <EmployeeApplicationRoutes
                  isAdminMode={isAdminMode}
                  employeeFormId={employeeFormId}
                  hasDeclinedCompletedPolicy={hasDeclinedCompletedPolicy}
                  availablePlans={availablePlans}
                  healthAccountBenefitStatus={healthAccountBenefitStatus}
                  isRenewal={isRenewal}
                />
              )}
            </Col>
          </Row>
        </Grid>
      </Div>
    );
  }

  return (
    <div>
      <Helmet titleTemplate="SimplyInsured - %s" defaultTitle="SimplyInsured" />
      <ResponsiveTopBar branding={branding || "default"} rightContent={rightContent} partnerLogo={partnerLogo} partnerName={partnerName} />
      <ProcessOverview />
      <Div className="min-h-dvh">
        {isLoading ? (
          <BasicLoading />
        ) : (
          <EmployeeApplicationRoutes
            isAdminMode={isAdminMode}
            employeeFormId={employeeFormId}
            hasDeclinedCompletedPolicy={hasDeclinedCompletedPolicy}
            availablePlans={availablePlans}
            healthAccountBenefitStatus={healthAccountBenefitStatus}
            isRenewal={isRenewal}
          />
        )}
      </Div>
      {employeeForm && (employeeForm.benefitsAdmin ? <BrokerFormFooter showSISupportLinks={isAdminMode} /> : <Footer />)}
      <Heartbeat />
      <ThemeInstaller />
    </div>
  );
};

const getPreviousStep = (steps, previousStepIndex) => {
  if (previousStepIndex < 0) {
    throw new Error(`No previous step: ${JSON.stringify(previousStepIndex)}`);
  }
  const previousStep = steps[previousStepIndex];
  if (previousStep.url === null) {
    return getPreviousStep(steps, previousStepIndex - 1);
  }
  return previousStep;
};

const backURL = (
  stepName: StepName,
  adminMode: boolean,
  employeeFormId: string,
  healthAccountBenefitStatus: string,
  availablePlans: Array<"medical" | "dental" | "vision" | "accident" | "life" | "term_life" | "whole_life" | "long_term_disability" | "short_term_disability">,
  isRenewal: boolean,
): string => {
  const steps = [
    { name: "basicInformation", url: basicInformationURL(adminMode, isRenewal, employeeFormId) },
    { name: "familyInformation", url: familyInformationURL(adminMode, isRenewal, employeeFormId) },
    { name: "chooseMedicalPlan", url: availablePlans.includes("medical") ? chooseMedicalPlanURL(adminMode, isRenewal, employeeFormId) : null },
    { name: "chooseDentalPlan", url: availablePlans.includes("dental") ? chooseDentalPlanURL(adminMode, isRenewal, employeeFormId) : null },
    { name: "chooseVisionPlan", url: availablePlans.includes("vision") ? chooseVisionPlanURL(adminMode, isRenewal, employeeFormId) : null },
    { name: "chooseAccidentPlan", url: availablePlans.includes("accident") ? chooseAccidentPlanURL(adminMode, isRenewal, employeeFormId) : null },
    { name: "chooseLifePlan", url: availablePlans.includes("life") ? chooseLifePlanURL(adminMode, isRenewal, employeeFormId) : null },
    { name: "chooseTermLifePlan", url: availablePlans.includes("term_life") ? chooseTermLifePlanURL(adminMode, isRenewal, employeeFormId) : null },
    { name: "chooseWholeLifePlan", url: availablePlans.includes("whole_life") ? chooseWholeLifePlanURL(adminMode, isRenewal, employeeFormId) : null },
    {
      name: "chooseShortTermDisabilityPlan",
      url: availablePlans.includes("short_term_disability") ? chooseShortTermDisabilityPlanURL(adminMode, isRenewal, employeeFormId) : null,
    },
    {
      name: "chooseLongTermDisabilityPlan",
      url: availablePlans.includes("long_term_disability") ? chooseLongTermDisabilityPlanURL(adminMode, isRenewal, employeeFormId) : null,
    },
    {
      name: "healthAccountInformation",
      url: ["done", "incomplete"].includes(healthAccountBenefitStatus) ? healthAccountInformationURL(adminMode, isRenewal, employeeFormId) : null,
    },
    { name: "additionalInformation", url: additionalInformationURL(adminMode, isRenewal, employeeFormId) },
    { name: "sign", url: signURL(adminMode, isRenewal, employeeFormId) },
    { name: "done", url: doneURL(adminMode, isRenewal, employeeFormId) },
  ];
  const currentStepIndex = steps.findIndex((s) => s.name === stepName);
  if (currentStepIndex === null) {
    throw new Error(`Unknown step: ${JSON.stringify(stepName)}`);
  }

  const previousStepIndex = currentStepIndex - 1;
  if (previousStepIndex < 0) {
    throw new Error(`No previous step: ${JSON.stringify(stepName)}`);
  }
  const previousStep = getPreviousStep(steps, previousStepIndex);
  return previousStep.url;
};

const EmployeeApplicationRoutes = ({ isAdminMode, employeeFormId, hasDeclinedCompletedPolicy, availablePlans, healthAccountBenefitStatus, isRenewal }) => (
  <ScrollToTop>
    {hasDeclinedCompletedPolicy ? (
      <Switch>
        <Route path={nestedEmployeeApplicationRoutes("/done")} exact children={(props) => <DonePage {...props} />} />

        <Route children={() => <Redirect to={doneURL(isAdminMode, false, employeeFormId, true)} />} />
      </Switch>
    ) : (
      <Switch>
        <Route
          path={nestedEmployeeApplicationRoutes("/")}
          exact
          children={(props) => <BasicInformationPage isRenewal={false} availablePlans={availablePlans} {...props} />}
        />

        <Route
          path={nestedEmployeeApplicationRoutes("/family-information")}
          exact
          children={(props) => (
            <FamilyInformationPage
              backLink={backURL("familyInformation", isAdminMode, employeeFormId, healthAccountBenefitStatus, availablePlans, isRenewal)}
              onClickBack={() => {
                AnalyticsTracking.trackEvent("back_button_clicked", {
                  application: "employee_application",
                  location: "family_information_page",
                });
              }}
              {...props}
            />
          )}
        />

        <Route path={nestedEmployeeApplicationRoutes("/choose-medical-coverage")} exact>
          <ChooseMedicalCoveragePage
            backLink={backURL("chooseMedicalPlan", isAdminMode, employeeFormId, healthAccountBenefitStatus, availablePlans, isRenewal)}
            onClickBack={() => {
              AnalyticsTracking.trackEvent("back_button_clicked", {
                application: "employee_application",
                location: "choose_medical_coverage_page",
              });
            }}
          />
        </Route>

        <Route
          path={nestedEmployeeApplicationRoutes("/choose-dental-coverage")}
          exact
          children={(props) => (
            <ChooseCoveragePage
              type="dental"
              backLink={backURL("chooseDentalPlan", isAdminMode, employeeFormId, healthAccountBenefitStatus, availablePlans, isRenewal)}
              onClickBack={() => {
                AnalyticsTracking.trackEvent("back_button_clicked", {
                  application: "employee_application",
                  location: "choose_dental_coverage_page",
                });
              }}
              {...props}
            />
          )}
        />

        <Route
          path={nestedEmployeeApplicationRoutes("/choose-vision-coverage")}
          exact
          children={(props) => (
            <ChooseCoveragePage
              type="vision"
              backLink={backURL("chooseVisionPlan", isAdminMode, employeeFormId, healthAccountBenefitStatus, availablePlans, isRenewal)}
              onClickBack={() => {
                AnalyticsTracking.trackEvent("back_button_clicked", {
                  application: "employee_application",
                  location: "choose_vision_coverage_page",
                });
              }}
              {...props}
            />
          )}
        />

        <Route path={nestedEmployeeApplicationRoutes("/choose-accident-coverage")} exact>
          <ChooseAccidentCoveragePage
            backLink={backURL("chooseAccidentPlan", isAdminMode, employeeFormId, healthAccountBenefitStatus, availablePlans, isRenewal)}
            onClickBack={() => {
              AnalyticsTracking.trackEvent("back_button_clicked", {
                application: "employee_application",
                location: "choose_accident_coverage_page",
              });
            }}
          />
        </Route>

        <Route path={nestedEmployeeApplicationRoutes("/choose-life-coverage")} exact>
          <ChooseLifeCoveragePage
            backLink={backURL("chooseLifePlan", isAdminMode, employeeFormId, healthAccountBenefitStatus, availablePlans, isRenewal)}
            onClickBack={() => {
              AnalyticsTracking.trackEvent("back_button_clicked", {
                application: "employee_application",
                location: "choose_life_coverage_page",
              });
            }}
          />
        </Route>

        <Route path={nestedEmployeeApplicationRoutes("/choose-term-life-coverage")} exact>
          <ChooseTermLifeCoveragePage
            backLink={backURL("chooseTermLifePlan", isAdminMode, employeeFormId, healthAccountBenefitStatus, availablePlans, isRenewal)}
            onClickBack={() => {
              AnalyticsTracking.trackEvent("back_button_clicked", {
                application: "employee_application",
                location: "choose_term_life_coverage_page",
              });
            }}
          />
        </Route>

        <Route path={nestedEmployeeApplicationRoutes("/choose-whole-life-coverage")} exact>
          <ChooseWholeLifeCoveragePage
            backLink={backURL("chooseWholeLifePlan", isAdminMode, employeeFormId, healthAccountBenefitStatus, availablePlans, isRenewal)}
            onClickBack={() => {
              AnalyticsTracking.trackEvent("back_button_clicked", {
                application: "employee_application",
                location: "choose_whole_life_coverage_page",
              });
            }}
          />
        </Route>

        <Route path={nestedEmployeeApplicationRoutes("/choose-short-term-disability-coverage")} exact>
          <ChooseShortTermDisabilityCoveragePage
            backLink={backURL("chooseShortTermDisabilityPlan", isAdminMode, employeeFormId, healthAccountBenefitStatus, availablePlans, isRenewal)}
            onClickBack={() => {
              AnalyticsTracking.trackEvent("back_button_clicked", {
                application: "employee_application",
                location: "choose_short_term_disability_coverage_page",
              });
            }}
          />
        </Route>

        <Route path={nestedEmployeeApplicationRoutes("/choose-long-term-disability-coverage")} exact>
          <ChooseLongTermDisabilityCoveragePage
            backLink={backURL("chooseLongTermDisabilityPlan", isAdminMode, employeeFormId, healthAccountBenefitStatus, availablePlans, isRenewal)}
            onClickBack={() => {
              AnalyticsTracking.trackEvent("back_button_clicked", {
                application: "employee_application",
                location: "choose_long_term_disability_coverage_page",
              });
            }}
          />
        </Route>

        <Route path={nestedEmployeeApplicationRoutes("/health-account-information")} exact>
          <HealthAccountInformationPage
            backLink={backURL("healthAccountInformation", isAdminMode, employeeFormId, healthAccountBenefitStatus, availablePlans, isRenewal)}
            onClickBack={() => {
              AnalyticsTracking.trackEvent("back_button_clicked", {
                application: "employee_application",
                location: "health_account_information_page",
              });
            }}
          />
        </Route>

        <Route
          path={nestedEmployeeApplicationRoutes("/additional-information")}
          exact
          children={(props) => (
            <AdditionalInformationPage
              backLink={backURL("additionalInformation", isAdminMode, employeeFormId, healthAccountBenefitStatus, availablePlans, isRenewal)}
              onClickBack={() => {
                AnalyticsTracking.trackEvent("back_button_clicked", {
                  application: "employee_application",
                  location: "additional_information_page",
                });
              }}
              {...props}
            />
          )}
        />

        <Route
          path={nestedEmployeeApplicationRoutes("/sign")}
          exact
          children={(props) => (
            <SignaturePage
              backLink={backURL("sign", isAdminMode, employeeFormId, healthAccountBenefitStatus, availablePlans, isRenewal)}
              basicInfoLink={basicInformationURL(isAdminMode, false, employeeFormId)}
              onClickBack={() => {
                AnalyticsTracking.trackEvent("back_button_clicked", {
                  application: "employee_application",
                  location: "signature_page",
                });
              }}
              {...props}
            />
          )}
        />

        <Route path={nestedEmployeeApplicationRoutes("/done")} exact children={(props) => <DonePage {...props} />} />

        <Route
          path={nestedEmployeeApplicationRoutes("/jump")}
          exact
          children={(props) => <JumpPage isAdminMode={isAdminMode} employeeFormId={employeeFormId} {...props} />}
        />

        <Route children={NotFoundPage} />
      </Switch>
    )}
  </ScrollToTop>
);

export default EmployeeApplication;
